import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { BASE_API_URL } from "../../config/config";
import { Logger } from '../../utils/logger';
import "../login/onboarding.css";

// Same category options from your constants
const BusinessCategoryOptions = [
  {name: 'Real Estate', value: 'real_estate'},
  {name: 'Insurance Agents', value: 'insurance_agents'},
  {name: 'Financial Services', value: 'financial_services'},
  {name: 'Digital Marketing Agency', value: 'digital_marketing_agency'},
  {name: 'Event Management', value: 'event_management'},
  {name: 'Creator/Influencer/Online Coach', value: 'creator_influencer_online_coach'},
  {name: 'Construction and Interior Design', value: 'construction_interior_design'},
  {name: 'Health and Wellness', value: 'health_wellness'},
  {name: 'Education and Training Institutes', value: 'education_training_institutes'},
  {name: 'IT Services and Software Companies', value: 'it_services_software_companies'},
  {name: 'Manufacturing & B2B Sales', value: 'manufacturing_b2b_sales'},
  {name: 'Hospitality and Tourism', value: 'hospitality_tourism'},
  {name: 'Automotive Dealerships', value: 'automotive_dealerships'},
  {name: 'Telecalling Team', value: 'telecalling_team'},
  {name: 'Services Business', value: 'services_business'},
  {name: 'E-commerce', value: 'e_commerce'},
  {name: 'Travel Consultancy', value: 'travel_consultancy'},
  {name: 'Others', value: 'others'}
];

const TeamSizeOptions = [
  { label: 'Just me', value: '1', description: 'Solo Entrepreneur' },
  { label: '2-5', value: '5', description: 'Small Team' },
  { label: '6-10', value: '10', description: 'Growing Team' },
  { label: '10-20', value: '20', description: 'Medium Team' },
  { label: '20+', value: '50', description: 'Large Team' }
];

const Signup = () => {
  Logger.log('[Signup] Component mounted');
  Logger.log('[Signup] Current localStorage token:', localStorage.getItem('auth_token'));
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  // Determine login method - if phone exists in localStorage, user logged in via phone
  // If email exists but no phone, user logged in via Google/Facebook
  const loginViaPhone = !!localStorage.getItem("phone");
  const loginViaSocial = !loginViaPhone && !!localStorage.getItem("email");

  const [formData, setFormData] = useState({
    firstName: localStorage.getItem("userName") || "",
    email: localStorage.getItem("email") || "",
    phone: localStorage.getItem("phone") || "",
    countryCode: localStorage.getItem("countryCode") || "91",
    companyName: "",
    teamSize: "",
    businessCategory: ""
  });

  const [isPhoneValid, setIsPhoneValid] = useState(true);

  const validatePhoneNumber = (number: string): boolean => {
    const phoneNumberPattern = /^[1-9]\d{9}$/;
    return phoneNumberPattern.test(number);
  };

  const handlePhoneChange = (value: string, country: any) => {
    const newCountryCode = country.dialCode;
    const numberWithoutCode = value.slice(newCountryCode.length);

    setFormData(prev => ({
      ...prev,
      phone: numberWithoutCode,
      countryCode: newCountryCode
    }));
    setIsPhoneValid(validatePhoneNumber(numberWithoutCode));
  };

  const handleFirstStep = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.firstName.trim()) {
      toast.error("Please enter your name");
      return;
    }

    // If user logged in via phone, require email
    if (loginViaPhone) {
      if (!formData.email.trim()) {
        toast.error("Email is required");
        return;
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        toast.error("Please enter a valid email");
        return;
      }
    }

    // If user logged in via Google/Facebook, require phone number
    if (loginViaSocial) {
      if (!formData.phone.trim()) {
        toast.error("Phone number is required");
        return;
      }

      if (!validatePhoneNumber(formData.phone)) {
        toast.error("Please enter a valid phone number");
        setIsPhoneValid(false);
        return;
      }
    }

    setStep(2);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    Logger.log('[Signup] Pre-submission token check:', localStorage.getItem('auth_token'));
    Logger.log('[Signup] Form data being submitted:', formData);

    // Full validation before API call - ensure BOTH phone and email are present
    if (!formData.firstName.trim() || !formData.email.trim() || !formData.phone.trim() ||
        !formData.companyName.trim() || !formData.businessCategory || !formData.teamSize) {
      toast.error("Please fill in all required fields");
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error("Please enter a valid email");
      return;
    }

    // Validate phone number
    if (!validatePhoneNumber(formData.phone)) {
      toast.error("Please enter a valid phone number");
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem("auth_token");

      Logger.log('[Signup] Token at API call:', token);
      Logger.log('[Signup] All localStorage keys:', Object.keys(localStorage));

      if (!token) {
        Logger.log('[Signup] Token missing - localStorage dump:', JSON.stringify(localStorage));
        throw new Error("Authentication token not found");
      }

      const response = await fetch(`${BASE_API_URL}/user/onboard`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `${token}`
        },
        body: JSON.stringify({
          profile: {
            firstName: formData.firstName.trim(),
            email: formData.email.trim(),
            phone: formData.phone,
            countryCode: formData.countryCode
          },
          organization: {
            name: formData.companyName.trim(),
            teamSize: formData.teamSize,
            businessCategory: formData.businessCategory
          }
        })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Failed to update profile");

      toast.success("Profile completed successfully!");

      // Update localStorage with the new phone/email data
      localStorage.setItem("phone", formData.phone);
      localStorage.setItem("countryCode", formData.countryCode);
      localStorage.setItem("email", formData.email);

      // Set flag to show trial welcome modal after navigation
      localStorage.setItem("show_trial_welcome_modal", "true");

      navigate("/leads", { replace: true });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Error while updating profile");
    } finally {
      setLoading(false);
    }
  };
  

  return (
    <div className="onboarding-container">
      <div className="onboarding-card">
        {/* Logo */}
        <div className="onboarding-logo">
          <img src="assets/img/3sigma_logo.png" alt="3Sigma CRM" />
        </div>

        {step === 1 ? (
          <>
            {/* Header */}
            <div className="onboarding-header">
              <h2>Get Started with 3Sigma</h2>
              <p className="onboarding-subtitle">First, tell us about yourself</p>
            </div>

            {/* Step Indicator */}
            <div className="onboarding-steps">
              <div className="onboarding-step active">1</div>
              <div className="onboarding-step-line"></div>
              <div className="onboarding-step">2</div>
            </div>

            {/* Step 1 Form */}
            <form onSubmit={handleFirstStep} className="onboarding-form">
              <div className="onboarding-form-group">
                <label>Name</label>
                <input
                  type="text"
                  className="onboarding-input"
                  name="firstName"
                  placeholder="Enter your name"
                  value={formData.firstName}
                  onChange={handleChange}
                />
              </div>

              {/* Show email field if user logged in via phone */}
              {loginViaPhone && (
                <div className="onboarding-form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    className="onboarding-input"
                    name="email"
                    placeholder="Enter your email"
                    value={formData.email}
                    onChange={handleChange}
                  />
                </div>
              )}

              {/* Show phone field if user logged in via Google/Facebook */}
              {loginViaSocial && (
                <div className="onboarding-form-group">
                  <label>Phone Number</label>
                  <div className="onboarding-phone-container">
                    <PhoneInput
                      country="in"
                      value={`${formData.countryCode}${formData.phone}`}
                      onChange={handlePhoneChange}
                      inputClass={!isPhoneValid ? "is-invalid" : ""}
                      containerClass="react-tel-input"
                    />
                    {!isPhoneValid && (
                      <span className="onboarding-error-text">
                        Please enter a valid mobile number
                      </span>
                    )}
                  </div>
                </div>
              )}

              <button type="submit" className="onboarding-btn-primary">
                Continue
              </button>
            </form>
          </>
        ) : (
          <>
            {/* Header */}
            <div className="onboarding-header">
              <h2>Tell Us About Your Business</h2>
              <p className="onboarding-subtitle">Set up your 3Sigma account</p>
            </div>

            {/* Step Indicator */}
            <div className="onboarding-steps">
              <div className="onboarding-step completed">1</div>
              <div className="onboarding-step-line completed"></div>
              <div className="onboarding-step active">2</div>
            </div>

            {/* Step 2 Form */}
            <form onSubmit={handleSubmit} className="onboarding-form">
              <div className="onboarding-form-group">
                <label>Organisation Name</label>
                <input
                  type="text"
                  className="onboarding-input"
                  name="companyName"
                  placeholder="Enter your organisation name"
                  value={formData.companyName}
                  onChange={handleChange}
                />
              </div>

              <div className="onboarding-form-group">
                <label>Business Category</label>
                <select
                  className="onboarding-select"
                  name="businessCategory"
                  value={formData.businessCategory}
                  onChange={handleChange}
                >
                  <option value="">Select business category</option>
                  {BusinessCategoryOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="onboarding-form-group">
                <label>Team Size</label>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                  gap: '12px',
                  marginTop: '12px'
                }}>
                  {TeamSizeOptions.map(option => (
                    <div
                      key={option.value}
                      className={`team-size-card ${formData.teamSize === option.value ? 'selected' : ''}`}
                      onClick={() => setFormData(prev => ({ ...prev, teamSize: option.value }))}
                      style={{
                        padding: '20px',
                        border: `2px solid ${formData.teamSize === option.value ? '#0056fe' : '#e2e8f0'}`,
                        borderRadius: '12px',
                        cursor: 'pointer',
                        background: formData.teamSize === option.value ? '#ecf4fe' : 'white',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <div style={{ fontWeight: 600, marginBottom: '4px', color: '#0f172a' }}>
                        {option.label}
                      </div>
                      <div style={{ fontSize: '14px', color: '#64748b' }}>
                        {option.description}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="onboarding-button-group">
                <button
                  type="button"
                  className="onboarding-btn-secondary"
                  onClick={() => setStep(1)}
                >
                  Back
                </button>
                <button
                  type="submit"
                  className="onboarding-btn-primary"
                  disabled={loading}
                >
                  {loading ? (
                    <span className="onboarding-loading">
                      <span className="onboarding-spinner"></span>
                      Please wait...
                    </span>
                  ) : (
                    "Let's Go"
                  )}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default Signup;